import React, { createContext, useContext, useState, useCallback } from 'react';

// Notification types
export interface AppNotification {
    id: string;
    type: 'success' | 'info' | 'warning' | 'error';
    title: string;
    message: string;
    timestamp: Date;
    read: boolean;
    icon?: string;
}

interface NotificationContextType {
    notifications: AppNotification[];
    unreadCount: number;
    addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotification: (id: string) => void;
    clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);

    const addNotification = useCallback((notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
        const newNotification: AppNotification = {
            ...notification,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            timestamp: new Date(),
            read: false,
        };

        setNotifications(prev => [newNotification, ...prev]);

        // Browser notification if permission granted
        if (Notification.permission === 'granted') {
            new Notification(notification.title, {
                body: notification.message,
                icon: '/supervisor-logo-2026.png',
                badge: '/supervisor-logo-2026.png',
                tag: 'supervisoregitim-notification',
            });
        }

        // Auto-remove after 10 seconds for toast display
        // setTimeout(() => {
        //     setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
        // }, 10000);
    }, []);

    const markAsRead = useCallback((id: string) => {
        setNotifications(prev =>
            prev.map(n => (n.id === id ? { ...n, read: true } : n))
        );
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    const clearNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                addNotification,
                markAsRead,
                markAllAsRead,
                clearNotification,
                clearAllNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

// Toast Notification Component
export const NotificationToast: React.FC = () => {
    const { notifications, clearNotification } = useNotifications();
    const recentNotifications = notifications.filter(n => !n.read).slice(0, 3);

    if (recentNotifications.length === 0) return null;

    const getTypeStyles = (type: AppNotification['type']) => {
        switch (type) {
            case 'success':
                return 'bg-green-500 border-green-600';
            case 'info':
                return 'bg-blue-500 border-blue-600';
            case 'warning':
                return 'bg-yellow-500 border-yellow-600';
            case 'error':
                return 'bg-red-500 border-red-600';
            default:
                return 'bg-gray-500 border-gray-600';
        }
    };

    const getTypeIcon = (type: AppNotification['type'], icon?: string) => {
        if (icon) return icon;
        switch (type) {
            case 'success':
                return '✅';
            case 'info':
                return 'ℹ️';
            case 'warning':
                return '⚠️';
            case 'error':
                return '❌';
            default:
                return '🔔';
        }
    };

    return (
        <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-sm">
            {recentNotifications.map(notification => (
                <div
                    key={notification.id}
                    className={`${getTypeStyles(notification.type)} text-white p-4 rounded-xl shadow-2xl border-2 animate-slide-in-right flex items-start gap-3`}
                >
                    <span className="text-2xl">{getTypeIcon(notification.type, notification.icon)}</span>
                    <div className="flex-1">
                        <h4 className="font-bold text-sm">{notification.title}</h4>
                        <p className="text-xs text-white/90 mt-1">{notification.message}</p>
                    </div>
                    <button
                        onClick={() => clearNotification(notification.id)}
                        className="text-white/70 hover:text-white transition-colors"
                    >
                        ✕
                    </button>
                </div>
            ))}
        </div>
    );
};

// Notification Bell Dropdown Component
export const NotificationBell: React.FC = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);

    const formatTime = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Az önce';
        if (minutes < 60) return `${minutes} dk önce`;
        if (hours < 24) return `${hours} saat önce`;
        return `${days} gün önce`;
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
                <span className="text-2xl">🔔</span>
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-2xl border z-50 overflow-hidden animate-scale-in">
                        <div className="p-4 border-b bg-gradient-to-r from-blue-900 to-indigo-700 text-white">
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold">Bildirimler</h3>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="text-xs text-white/80 hover:text-white"
                                    >
                                        Tümünü okundu işaretle
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="max-h-80 overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="p-8 text-center text-gray-400">
                                    <span className="text-4xl block mb-2">🔕</span>
                                    <p>Bildirim yok</p>
                                </div>
                            ) : (
                                notifications.slice(0, 10).map(notification => (
                                    <div
                                        key={notification.id}
                                        onClick={() => markAsRead(notification.id)}
                                        className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${!notification.read ? 'bg-blue-50' : ''
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="text-xl">{notification.icon || '🔔'}</span>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-sm text-gray-800 truncate">
                                                    {notification.title}
                                                </h4>
                                                <p className="text-xs text-gray-500 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <span className="text-xs text-gray-400 mt-1 block">
                                                    {formatTime(notification.timestamp)}
                                                </span>
                                            </div>
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    clearNotification(notification.id);
                                                }}
                                                className="text-gray-300 hover:text-red-500 transition-colors"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
